import { useCallback } from 'react';
import { useGotoRecoilSnapshot } from 'recoil'; // todo expose on @backpackjs/storefront

import store, { useRecoilCallback, useRecoilValue } from '@store';

const DEBUG = true;

export const useMenuDrawer = (cb = () => {}, data = []) => {
  const gotoSnapshot = useGotoRecoilSnapshot();
  const hovered = useRecoilValue(store.hoveredItem);
  const menuDrawer = useRecoilValue(store.menuDrawer);

  // on click, close the drawer and navigate to url
  const closeMenuDrawer = useRecoilCallback(({ snapshot }) => async () => {
    const release = snapshot.retain();
    try {
      const updatedState = snapshot.map(({ set }) => {
        set(store.hoveredItem, null);
        set(store.menuDrawer, false);
      });
      // update state
      gotoSnapshot(updatedState);
    } catch (error) {
      console.log('closeMenuDrawer', error.message);
      throw error;
    } finally {
      release();
    }
  });

  // on link hover, clear a previous un-hover timer
  const clearUnHoverTimer = useCallback((id) => {
    if (window.unHover) {
      clearTimeout(window.unHover);
      window.unHover = null;
    }
  }, []);

  const hoverDrawer = useCallback(() => {
    clearUnHoverTimer();
  }, []);

  const hoverItem = useRecoilCallback(({ snapshot }) => async (hoveredItem) => {
    const release = snapshot.retain();

    try {
      let updatedState;

      if (hoveredItem) {
        clearUnHoverTimer();

        const { medias = [] } = hoveredItem;
        const hoveredSubLinks = medias.filter(Boolean);
        const hoveredSubLinksCount = hoveredSubLinks.length;

        // if direct link close shell
        if (!hoveredSubLinksCount) {
          // console.log('direct link: closed drawer and setting hoverItem to', hoverItemData);
          updatedState = snapshot.map(({ set }) => {
            set(store.hoveredItem, hoveredItem);
            set(store.menuDrawer, false);
          });
        } else {
          // console.log('nested link: opened drawer and setting hoverItem to', hoverItemData);
          updatedState = snapshot.map(({ set }) => {
            set(store.hoveredItem, hoveredItem);
            set(store.menuDrawer, true);
          });
        }
      }
      // hoveredItem is null (reset)
      else {
        // console.log('no link: closed drawer and setting hoveredItem to', null)
        updatedState = snapshot.map(({ set }) => {
          set(store.hoveredItem, hoveredItem);
          set(store.menuDrawer, false);
        });
      }

      // update state
      gotoSnapshot(updatedState);
    } catch (error) {
      console.log('hoverItem', error.message);
      throw error;
    } finally {
      release();
    }
  });

  const unHoverItem = useCallback(() => {
    // if we have a previous un-hover we clear it
    clearUnHoverTimer();

    // we set a new un-hover timer, that can be cancelled
    // by the mouseEnter o another link or the MenuDrawer enter
    window.unHover = setTimeout(() => {
      hoverItem(null); // closes the drawer
      clearUnHoverTimer();
    }, 100);
    // console.log('created timer: via unHoverItem', JSON.stringify(window.unHover))
  }, []);

  const unHoverDrawer = useRecoilCallback(({ snapshot }) => () => {
    const release = snapshot.retain();

    // we set a new un-hover timer, that can be cancelled by the mouseEnter
    window.unHover = setTimeout(async function () {
      // console.log('executing timer: unHoverDrawer after 500ms setting hoveredItem to null')
      try {
        const updatedState = snapshot.map(({ set }) => {
          set(store.menuDrawer, false);
          set(store.hoveredItem, null);
        });

        gotoSnapshot(updatedState);
        clearUnHoverTimer();
      } catch (error) {
        console.log('unHoverDrawer', error.message);
        throw error;
      } finally {
        release();
      }
    }, 100);

    // console.log('created timer: via unHoverDrawer', JSON.stringify(window.unHover))
  });

  return [
    // state
    {
      hovered, // used to trigger the drawer
      menuDrawer, // used for animations
    },
    // actions
    {
      hoverItem,
      hoverDrawer,
      unHoverItem,
      unHoverDrawer,
      closeMenuDrawer,
    },
  ];
};
